// The P2 cull (APP_VISIBILITY §3e, cdb-cull): in the main pass, a draw unit
// that is PROVABLY hidden is not drawn. The visibility DECISION is made once
// per key of its own VisFrame (visframe.js: the eye box + rotation bound of
// the mode, the scene generations the occluders read) against the
// conservative depth buffer (conservativedepth.js); a stable frame reuses
// every unit's decision unless the unit itself changed (its version: a new
// geometry, a re-draped position buffer, a refilled instance buffer).
//
// How it hides: main.js arms the cull around the main render; three calls
// scene.onBeforeRender AFTER it posed the XR array camera and BEFORE its own
// frustum walk, so `visible = false` there skips the unit for this draw only
// (restore() puts it back in scene.onAfterRender / main.js's finally). No
// family's own visibility, residency or streaming state is touched: a hidden
// unit stays loaded and draws the frame the view leaves its occluder (§3f).
// The optic passes (scope, monocular, the band's monoCam) are not culled.
//
// Units (§3e; the upper bound of what each DRAWS): ground-tree nodes after
// the selection (the node square, y from the stitched geometry and the
// parent's surface the geomorph moves to, + the δ code's 4.4 % step), forest
// volume groups (geometry + the VS lid pad), far-building chunks, near tree
// chunks (every instance's transformed box + the sway pad), structure
// meshes (re-measured when the drape rewrites them), water sheets / rivers
// and the static lift meshes (cables, pylons). Out: building GLB tiles (the
// occluders), the ring (being rewritten: a TODO in APP_VISIBILITY), hero
// trees / shrubs / grass / furniture (near sets), movers.
//
// Occluders: the terrain (the horizon table over the L5 lower envelope,
// lowered by the drawn nodes' measured undershoot; bore footprints masked;
// only when the drawn set covers the frame and the eye stands above the
// drawn ground) and the inset walls of collision prisms within 200 m whose
// GLB tile draws in this pass, the eye outside the footprint.
//
// P3 cell runs (§3e, cell-runs): a unit whose geometry carries
// `userData.cells` (cellruns.js cellSort: its triangles ordered by the 128 m world
// cell of their centroid, Morton order, each cell an element range + the AABB
// of what it draws) is drawn as its VISIBLE runs: a cell is off when its box
// is outside the key's dilated planes or hidden in the CDB (an aligned 8 × 8
// block of cells — 1024 m — is tested first); adjacent drawn cells merge into
// one run, the smallest gaps merge until ≤ MAX_RUNS runs remain (a merged gap
// is drawn: more, never less); at one run (the quest) only the ends are
// tested (_endGroups: the same range). three draws geometry.groups only for a
// material ARRAY, so for this pass the mesh's material becomes [its own
// material] (one program, one state) and groups the runs; restore() puts
// both back. Cell units: ground-tree nodes (groundtree.js _geoFrom), the
// building GLB meshes (buildings.js _install; the occluders' own meshes — a
// cell hidden by a prism holds none of that prism's front surface: the
// surface a ray crosses before it enters the inset prism is nearer than
// the prism's exit, so the cell that holds it is never behind that cover),
// forest-volume groups (forestvol.js _build; a 1024 m shell tile is one
// aligned block). `cells = false` (probe hook) draws whole units (P2 only).

import { VisFrame, poseOf, boundsFor, quatOfMatrix } from './visframe.js';
import { CDB, ConservativeDepth, HorizonTable, lowerGroundFromNodes, horizonMargin, itemWalls, nearFootprint, viewRect } from './conservativedepth.js';
import { MAX_RUNS, cellRuns } from './cellruns.js';
import { classDraw } from './device.js';

// the run cap × the class draw rule (device.js CLASS_DRAW runs; quest-draws
// 2026-11-01: a headset frame is CPU-bound by draw submission, so quest 0.25
// = ONE run a unit — the smallest gaps all merge; a merged gap draws more
// triangles, never fewer: the same pixels, the P3 vertex saving traded for
// up to 3 draws a unit). Desktop / phone: MAX_RUNS, as before.
export const runsCap = (cls) => Math.max(1, Math.round(MAX_RUNS * classDraw('runs', cls)));

const HZ_MS = 0.5;            // the horizon table's slice per frame (ms)
const NODE_M = 128;           // a ground-tree L0 node (groundm/lookup.js nodeSide)
const DQ_PAD = 1.1;           // the geomorph δ code: a 4.4 % step of ≤ 24.4 m (APP_GROUNDM)
const FV_PAD = 1.1;           // the forest volume's VS lid pad (1 m, forestvol.js) + 0.1
const SWAY_XZ = 3;            // tree sway (trees.js pad max(3, 0.035·w), w ≤ 85 m) …
const SWAY_Y = 1;
const WATER_Y = 0.5;
const FAMS = ['ground', 'volume', 'farbld', 'trees', 'structs', 'water', 'lifts', 'bld'];
const CELL_FAMS = ['ground', 'volume', 'bld'];

export class VisCull {
  // deps: getters for the managers (main.js creates them over the load):
  // terrain, buildings, hf, groundLower, roadnet, forest, structDraw,
  // farBuildings, aerialway, forestVol (→ the forest volume's group Map),
  // boreCut (corridor.js: the volumes the bake cut the ground tree with);
  // meta: the bake's meta.json (the frame W × H)
  constructor(deps, meta) {
    this.d = deps;
    this.W = meta.grid.W * meta.grid.cell_m;
    this.H = meta.grid.H * meta.grid.cell_m;
    this.enabled = true;
    this.vis = new VisFrame('cull');
    this.cdb = new ConservativeDepth();
    this.water = [];             // water sheet / river meshes (main.js adds them)
    this.lg = null;              // LowerGround (once L5 + the bore cut are in)
    this._cutGen = 0;
    this.hz = null;              // HorizonTable
    this._armed = false;
    this._mode = null;
    this._hidden = [];
    this.cells = true;           // P3 cell runs (a probe hook: false = whole units)
    this.maxRuns = runsCap();    // draws a unit at most (the class rule; built after detectDevice)
    this.ends = true;            // maxRuns 1: the run from both ends (_endGroups; false = every cell tested, a gate's reference)
    this.pass = null;            // the culled main pass's CDB (forestvol's depth cut), null outside it
    this.passKey = -1;
    this._swapped = [];          // [mesh, its material, its groups] of this pass
    this._rec = new WeakMap();   // unit → { kid, ver, hid, runs }
    this._boxes = new WeakMap(); // unit → { ver, lo, hi }
    this._walls = new WeakMap(); // collision item → its inset walls
    this._pose = {}; this._vp = [0, 0]; this._gens = {};
    this._kid = -1; this._ok = false;
    this._tgen = -1;             // terrain.gen the ground state below was made for
    this._nodes = [];            // the drawn nodes: { x0, z0, x1, z1, under }
    this._cover = false;         // the drawn set covers the frame
    this.stats = { frames: 0, keys: 0, ms: 0, msMax: 0, buildMs: 0, walls: 0, wallCells: 0, terrainCells: 0,
      terrain: false, hz: { builds: 0, ms: 0, stale: 0 }, fam: Object.fromEntries(FAMS.map((f) => [f, [0, 0]])),
      // per cell family: [units with cells, cells, cells off, draws]
      cells: Object.fromEntries(CELL_FAMS.map((f) => [f, [0, 0, 0, 0]])), err: null };
  }

  // main.js: the next render of the scene is the main pass (mode: the bound class)
  arm(mode) { this._armed = this.enabled; this._mode = mode; }
  disarm() { this._armed = false; this.restore(); }
  restore() {
    this.pass = null;
    for (const o of this._hidden) o.visible = true;
    this._hidden.length = 0;
    const W = this._swapped;
    for (let i = 0; i < W.length; i += 3) { W[i].material = W[i + 1]; W[i].geometry.groups = W[i + 2]; }
    W.length = 0;
  }

  // scene.onBeforeRender (camera: the pass camera; XR: the array camera)
  before(renderer, camera) {
    if (!this._armed) return;
    this._armed = false;
    const t0 = performance.now();
    try {
      this._run(renderer, camera);
      this.pass = this._ok ? this.cdb : null; this.passKey = this._kid;
    } catch (e) {
      // never a wrong cull: the cull switches itself off and says so
      this.restore();
      this.enabled = false;
      this.stats.err = String(e && e.message || e);
      console.error('[viscull] off:', e);
    }
    const ms = performance.now() - t0;
    this.stats.ms = ms;
    if (ms > this.stats.msMax) this.stats.msMax = ms;
  }

  _run(renderer, cam) {
    const d = this.d, S = this.stats;
    S.frames++;
    const eyes = cam.isArrayCamera ? cam.cameras : null;
    this._ground(cam);
    const t = d.terrain, b = d.buildings;
    const g = this._gens;
    g.t = t ? t.gen : -1;
    g.b = b ? b.drawnGen : -1;
    g.c = b && b.collider ? b.collider.gen : -1;
    g.h = this.hz ? this.hz.version : -1;
    g.l = this.lg ? 1 : 0;
    this._vp[0] = renderer.domElement.width; this._vp[1] = renderer.domElement.height;
    const v = this.vis.begin(poseOf(cam, this._pose, this._vp, eyes), g, boundsFor(this._mode));
    if (v.key.id !== this._kid) {
      const t0 = performance.now();
      this._kid = v.key.id;
      S.keys++;
      this._ok = this._build(v.key, cam, eyes);
      S.buildMs = performance.now() - t0;
    }
    for (const f of FAMS) { S.fam[f][0] = 0; S.fam[f][1] = 0; }
    for (const f of CELL_FAMS) S.cells[f].fill(0);
    if (!this._ok) return;
    this._units(cam);
  }

  // the drawn set's ground state (on a terrain.gen change) and the horizon
  // table's upkeep (every frame, sliced)
  _ground(cam) {
    const d = this.d, t = d.terrain, gm = t && t.gm;
    // the bore cut: registered by ways2.js _bores (boreRuns set after it;
    // null = nothing cut); a new cut generation is a new lower ground
    const rn = d.roadnet, cut = rn && rn.boreRuns !== undefined ? d.boreCut() : undefined, cg = cut ? cut.gen : 0;
    if (this.lg && cg !== this._cutGen) { this.lg = null; this.hz = null; }
    if (!this.lg) {
      const c = d.groundLower;
      if (!c || !c.env || cut === undefined) return;
      this.lg = lowerGroundFromNodes(c, this.W, this.H);
      this.lg.maskHoles(cut ? cut.vols : []);
      this._cutGen = cg;
      this.hz = new HorizonTable(this.lg);
    }
    if (!gm || !gm.index) return;
    if (t.gen !== this._tgen) {
      this._tgen = t.gen;
      this._groundState(gm);
      // a table made for smaller margins is no bound any more
      const hz = this.hz;
      if (hz.ready && !this._marginOk(hz.c, hz.margin)) { hz.ready = false; this.stats.hz.stale++; }
      if (hz._job && !this._marginOk(hz._job.c, hz._job.margin)) hz._job = null;
    }
    if (!this._cover) return;
    const m = cam.matrixWorld.elements, ex = m[12], ey = m[13], ez = m[14], hz = this.hz;
    // a new table when the eye is CDB.lead off the cell's centre, has risen
    // CDB.lead towards its top, or sunk 2 · CDB.yUp (a weaker, still valid table)
    const off = (c) => !c || Math.max(Math.abs(ex - c[0]), Math.abs(ez - c[2])) > CDB.lead
      || ey > c[1] - CDB.yUp + CDB.lead || ey < c[1] - 3 * CDB.yUp;
    if (off(hz.ready ? hz.c : null) && (!hz._job || off(hz._job.c))) {
      hz.start(ex, ey + CDB.yUp, ez, horizonMargin(ex, ez, this._nodes));
    }
    if (hz._job) {
      const t0 = performance.now();
      if (hz.step(HZ_MS)) this.stats.hz.builds++;
      this.stats.hz.ms += performance.now() - t0;
    }
  }

  _groundState(gm) {
    const nodes = this._nodes, ix = gm.index.nodes;
    nodes.length = 0;
    const NX = Math.ceil(this.W / NODE_M), NY = Math.ceil(this.H / NODE_M);
    // a drawn entry is a node ('k_tx_ty') or one quadrant of it ('k_tx_ty#q',
    // q = dy * 2 + dx; the node's mesh, its measured under): the
    // footprint is the quadrant's. Coverage per L0 node cell
    if (!this._covG || this._covG.length !== NX * NY) this._covG = new Uint8Array(NX * NY);
    const cov = this._covG;
    cov.fill(0);
    let area = 0, ok = true;
    for (const [key, n] of gm.drawn) {
      const e = ix.get(`${n.k}_${n.tx}_${n.ty}`);
      if (!e || !n.mesh || !(e.under >= 0)) { ok = false; continue; }
      const p = e.up, pp = p && p.up;
      // the node, its parent (the geomorph's target) and the grandparent (a
      // set that coarsens by a level keeps the table): their undershoot
      const under = Math.max(e.under, p ? p.under : 0, pp ? pp.under : 0);
      if (!(under >= 0)) { ok = false; continue; }
      const h = key.indexOf('#'), q = h < 0 ? -1 : +key.slice(h + 1);
      const fk = q < 0 ? n.k : n.k - 1, fx = q < 0 ? n.tx : 2 * n.tx + (q & 1), fy = q < 0 ? n.ty : 2 * n.ty + (q >> 1);
      if (!(fk >= 0)) { ok = false; continue; }
      const s = NODE_M << fk, u = 1 << fk;
      nodes.push({ x0: fx * s, z0: fy * s, x1: (fx + 1) * s, z1: (fy + 1) * s, under });
      // clamped to the frame's cells: a far node (tx / ty < 0, past W / H)
      // covers only its part inside (a negative index would alias a row)
      for (let j = Math.max(0, fy * u); j < Math.min(NY, (fy + 1) * u); j++) {
        for (let i = Math.max(0, fx * u); i < Math.min(NX, (fx + 1) * u); i++) if (!cov[j * NX + i]) { cov[j * NX + i] = 1; area++; }
      }
    }
    this._cover = ok && area === NX * NY;
  }

  _marginOk(c, used) {
    const now = horizonMargin(c[0], c[2], this._nodes);
    for (let i = 0; i < now.length; i++) if (now[i] > used[i]) return false;
    return true;
  }

  // the key's CDB: false = no cull for this key
  _build(key, cam, eyes) {
    const S = this.stats;
    const eyeP = eyes ? eyes.map((e) => ({ proj: e.projectionMatrix.elements, quat: quatOfMatrix(e.matrixWorld.elements, [0, 0, 0, 1]) })) : null;
    let rect;
    try { rect = viewRect(key, eyeP); } catch (e) { S.err = e.message; return false; }
    const hz = this.hz, terr = !!(this._cover && hz && hz.covers(key.lo, key.hi) && this._eyeAbove(key));
    const walls = this._wallsFor(key, cam);
    this.cdb.build(key, rect, terr ? hz : null, walls);
    S.terrain = terr; S.walls = this.cdb.stats.walls; S.wallCells = this.cdb.stats.wallCells; S.terrainCells = this.cdb.stats.terrain;
    return true;
  }

  // every eye of the box over the drawn ground under it: the drawn surface
  // (groundDrawn: the finest drawn node, morphed, triangle-exact; NaN over a
  // hole) at the box's corners and centre + CDB.eyeClear, which also holds
  // the PL surface's rise inside the box (slope · diagonal: D6's boxes are
  // ≤ 4 cm, the eye stands ≥ the player's radius off any wall)
  _eyeAbove(key) {
    const t = this.d.terrain;
    if (!t || !t.groundDrawn) return false;
    const { lo, hi } = key;
    let top = -Infinity;
    for (let i = 0; i < 5; i++) {
      const x = i === 4 ? (lo[0] + hi[0]) / 2 : i & 1 ? hi[0] : lo[0], z = i === 4 ? (lo[2] + hi[2]) / 2 : i & 2 ? hi[2] : lo[2];
      const y = t.groundDrawn(x, z);
      if (!Number.isFinite(y)) return false;
      if (y > top) top = y;
    }
    return lo[1] > top + CDB.eyeClear;
  }

  // the inset walls of the collision prisms near the eye whose GLB tile draws
  _wallsFor(key, cam) {
    const B = this.d.buildings, C = B && B.collider, out = [];
    if (!B || !C || !B.group || !B.group.visible) return out;
    const cx = (key.lo[0] + key.hi[0]) / 2, cz = (key.lo[2] + key.hi[2]) / 2, R = CDB.wallNear;
    const pad = 0.5 + Math.max(key.hi[0] - key.lo[0], key.hi[2] - key.lo[2]);
    const T = C.tileM;
    for (let ty = Math.floor((cz - R) / T); ty <= Math.floor((cz + R) / T); ty++) {
      for (let tx = Math.floor((cx - R) / T); tx <= Math.floor((cx + R) / T); tx++) {
        const k = `${tx}_${ty}`, root = B.tiles.get(k), items = C.tiles.get(k);
        if (!root || root === 'loading' || !root.visible || !items || !(root.layers.mask & cam.layers.mask)) continue;
        for (const it of items) {
          const [x0, z0, x1, z1] = it.bbox;
          const ox = Math.max(x0 - cx, 0, cx - x1), oz = Math.max(z0 - cz, 0, cz - z1);
          if (ox * ox + oz * oz > R * R) continue;
          if (ox <= pad && oz <= pad && nearFootprint(it, cx, cz, pad)) continue;
          let w = this._walls.get(it);
          if (!w) this._walls.set(it, (w = itemWalls(it)));
          for (const q of w) out.push(q);
        }
      }
    }
    return out;
  }

  // every unit of every family: its decision for this key (cached), hidden
  // ones invisible for this draw
  _units(cam) {
    const d = this.d;
    const t = d.terrain, gm = t && t.gm;
    if (gm && gm.index) {
      const ix = gm.index.nodes;
      for (const n of gm.drawn.values()) {
        const o = n.mesh;
        if (o && o.visible && o.parent) this._unit('ground', o, o.geometry.id, groundBox, n, ix);
      }
    }
    const fv = d.forestVol;
    if (fv) for (const g of fv.values()) { const o = g.mesh; if (o.visible && o.parent && o.parent.visible) this._unit('volume', o, o.geometry.id, volumeBox); }
    const fb = d.farBuildings;
    if (fb && fb.group && fb.group.visible) for (const o of fb.meshes.values()) if (o.visible && o.parent && o.frustumCulled) this._unit('farbld', o, o.geometry.id, meshBox);
    const fo = d.forest;
    if (fo && fo.chunks) {
      for (const grp of fo.chunks.values()) {
        if (!grp || !grp.visible) continue;
        for (const o of grp.children) {
          if (o.isInstancedMesh && o.visible && o.frustumCulled) this._unit('trees', o, o.instanceMatrix.version * 65536 + o.count, treeBox);
        }
      }
    }
    const sd = d.structDraw;
    if (sd && sd.groups) {
      for (const grp of sd.groups) {
        if (!grp.visible) continue;
        for (const o of grp.children) {
          if (o.isMesh && o.visible && o.frustumCulled) this._unit('structs', o, o.geometry.id * 1e7 + o.geometry.attributes.position.version, meshBox);
        }
      }
    }
    for (const o of this.water) if (o.visible && o.parent) this._unit('water', o, o.geometry.id, waterBox);
    const aw = d.aerialway;
    if (aw && aw.statics) for (const o of aw.statics) if (o.visible && o.parent) this._unit('lifts', o, o.geometry.id, meshBox);
    // the building GLB meshes (P3: units only through their cells' boxes)
    const B = d.buildings;
    if (B && B.group && B.group.visible && B.tiles) {
      for (const root of B.tiles.values()) {
        if (root === 'loading' || !root.visible || !(root.layers.mask & cam.layers.mask)) continue;
        for (const o of root.children) if (o.isMesh && o.visible && o.geometry.userData.cells) this._unit('bld', o, o.geometry.id, cellsBox);
      }
    }
  }

  _unit(fam, o, ver, boxFn, a, b) {
    const F = this.stats.fam[fam];
    F[0]++;
    let r = this._rec.get(o);
    if (!r || r.kid !== this._kid || r.ver !== ver) {
      let bx = this._boxes.get(o);
      if (!bx || bx.ver !== ver) {
        bx = { ver, lo: [Infinity, Infinity, Infinity], hi: [-Infinity, -Infinity, -Infinity] };
        boxFn(o, bx.lo, bx.hi, a, b);
        this._boxes.set(o, bx);
      }
      const hid = bx.lo[0] <= bx.hi[0] && bx.lo[1] <= bx.hi[1] && bx.lo[2] <= bx.hi[2] && this.cdb.test(bx.lo, bx.hi) === 'hidden';
      if (!r) this._rec.set(o, (r = { kid: 0, ver: 0, hid: false }));
      r.kid = this._kid; r.ver = ver; r.hid = hid;
    }
    if (r.hid) {
      o.visible = false;
      this._hidden.push(o);
      F[1]++;
      return;
    }
    const cs = o.geometry.userData.cells;
    if (cs && this.cells && !Array.isArray(o.material)) this._runs(fam, o, r, cs);
  }

  // a cell unit's visible runs for this key (memo'd on its record): every
  // cell off draws nothing, every cell on draws the mesh as it is
  _runs(fam, o, r, cs) {
    const C = this.stats.cells[fam];
    // a table that holds only in some state of its family (cs.live)
    if (cs.live && !cs.live()) return;
    C[0]++; C[1] += cs.cells.length;
    if (r.rk !== r.kid || r.rv !== r.ver || r.rc !== cs) {
      r.rk = r.kid; r.rv = r.ver; r.rc = cs;
      r.groups = this._cellGroups(cs, r);
    }
    C[2] += r.off;
    if (r.groups === null) { C[3]++; return; }
    if (!r.groups.length) { o.visible = false; this._hidden.push(o); return; }
    C[3] += r.groups.length;
    const mat = o.material;
    if (!r.mat) r.mat = [null];
    r.mat[0] = mat;
    this._swapped.push(o, mat, o.geometry.groups);
    o.material = r.mat;
    o.geometry.groups = r.groups;
  }

  // the runs of one cell unit as three groups (null: every cell on); `r.off`
  // the cells off
  _cellGroups(cs, r) {
    if (this.maxRuns === 1 && this.ends) return this._endGroups(cs, r);
    const planes = this.vis.planes(), cdb = this.cdb, cells = cs.cells, n = cells.length;
    const on = r.on && r.on.length === n ? r.on : (r.on = new Uint8Array(n));
    let off = 0;
    for (const b of cs.blocks) {
      const bo = boxOut(planes, b.lo, b.hi) || cdb.test(b.lo, b.hi) === 'hidden';
      for (let c = b.c0; c < b.c1; c++) {
        const x = bo ? 0 : b.c1 - b.c0 === 1 ? 1 : boxOut(planes, cells[c].lo, cells[c].hi) || cdb.test(cells[c].lo, cells[c].hi) === 'hidden' ? 0 : 1;
        on[c] = x;
        if (!x) off++;
      }
    }
    r.off = off;
    if (!off) return null;
    const runs = cellRuns(cells, on, this.maxRuns);
    return runs.map(([start, count]) => ({ start, count, materialIndex: 0 }));
  }

  // ONE run a unit (the quest class: runsCap 1, quest-cpu 2026-11-02): cellRuns
  // with maxRuns 1 merges every gap, so the run is the first drawn cell's start
  // to the last drawn cell's end — found by testing from each end (block first,
  // as above) and never the cells between, which draw either way. The same
  // element range as the full test (check-vis "one run"); `on` = drawn (the
  // ends' cells off, every cell between on), `r.off` = the ends' cells.
  _endGroups(cs, r) {
    const planes = this.vis.planes(), cdb = this.cdb, cells = cs.cells, blocks = cs.blocks, n = cells.length;
    const on = r.on && r.on.length === n ? r.on : (r.on = new Uint8Array(n));
    const cellOn = (b, c) => (b.c1 - b.c0 === 1 ? true : !(boxOut(planes, cells[c].lo, cells[c].hi) || cdb.test(cells[c].lo, cells[c].hi) === 'hidden'));
    const blockOut = (b) => boxOut(planes, b.lo, b.hi) || cdb.test(b.lo, b.hi) === 'hidden';
    let first = -1, last = -1;
    for (let i = 0; i < blocks.length && first < 0; i++) {
      const b = blocks[i];
      if (blockOut(b)) continue;
      for (let c = b.c0; c < b.c1; c++) if (cellOn(b, c)) { first = c; break; }
    }
    if (first < 0) { on.fill(0); r.off = n; return []; }
    for (let i = blocks.length - 1; i >= 0 && last < 0; i--) {
      const b = blocks[i];
      if (b.c1 <= first) break;
      if (blockOut(b)) continue;
      for (let c = b.c1 - 1; c >= Math.max(b.c0, first); c--) if (cellOn(b, c)) { last = c; break; }
    }
    on.fill(0, 0, first); on.fill(1, first, last + 1); on.fill(0, last + 1);
    r.off = first + n - 1 - last;
    if (!r.off) return null;
    const start = cells[first].start;
    return [{ start, count: cells[last].start + cells[last].count - start, materialIndex: 0 }];
  }
}

// a box wholly outside one of the planes [nx, ny, nz, c] (n·p + c ≥ 0 inside)
function boxOut(planes, lo, hi) {
  for (const p of planes) {
    if (p[0] * (p[0] > 0 ? hi[0] : lo[0]) + p[1] * (p[1] > 0 ? hi[1] : lo[1]) + p[2] * (p[2] > 0 ? hi[2] : lo[2]) + p[3] < 0) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// unit boxes (world AABB of what the unit can draw)

// the position attribute's local box (a Float32 array fast; else getX)
function posBox(geo, lo, hi) {
  const p = geo.attributes.position, n = p.count;
  if (p.array instanceof Float32Array && !p.isInterleavedBufferAttribute && p.itemSize === 3 && !p.normalized) {
    const A = p.array;
    for (let i = 0; i < 3 * n; i += 3) {
      const x = A[i], y = A[i + 1], z = A[i + 2];
      if (x < lo[0]) lo[0] = x; if (x > hi[0]) hi[0] = x;
      if (y < lo[1]) lo[1] = y; if (y > hi[1]) hi[1] = y;
      if (z < lo[2]) lo[2] = z; if (z > hi[2]) hi[2] = z;
    }
  } else {
    for (let i = 0; i < n; i++) {
      const x = p.getX(i), y = p.getY(i), z = p.getZ(i);
      if (x < lo[0]) lo[0] = x; if (x > hi[0]) hi[0] = x;
      if (y < lo[1]) lo[1] = y; if (y > hi[1]) hi[1] = y;
      if (z < lo[2]) lo[2] = z; if (z > hi[2]) hi[2] = z;
    }
  }
}

// the box [lo, hi] through the column-major 4 × 4 `m` (the exact AABB of the
// transformed box), into lo / hi
function xform(m, lo, hi) {
  const cx = (lo[0] + hi[0]) / 2, cy = (lo[1] + hi[1]) / 2, cz = (lo[2] + hi[2]) / 2;
  const hx = (hi[0] - lo[0]) / 2, hy = (hi[1] - lo[1]) / 2, hz = (hi[2] - lo[2]) / 2;
  for (let r = 0; r < 3; r++) {
    const c = m[r] * cx + m[4 + r] * cy + m[8 + r] * cz + m[12 + r];
    const e = Math.abs(m[r]) * hx + Math.abs(m[4 + r]) * hy + Math.abs(m[8 + r]) * hz;
    lo[r] = c - e; hi[r] = c + e;
  }
}

function meshBox(o, lo, hi) {
  posBox(o.geometry, lo, hi);
  if (lo[0] > hi[0]) return;
  xform(o.matrixWorld.elements, lo, hi);    // static: this tick's scene walk set it
}

// a cell unit (static, world coordinates): the union of its blocks' boxes
function cellsBox(o, lo, hi) {
  for (const b of o.geometry.userData.cells.blocks) {
    for (let k = 0; k < 3; k++) { if (b.lo[k] < lo[k]) lo[k] = b.lo[k]; if (b.hi[k] > hi[k]) hi[k] = b.hi[k]; }
  }
}

function waterBox(o, lo, hi) { meshBox(o, lo, hi); hi[1] += WATER_Y; }

function volumeBox(o, lo, hi) { meshBox(o, lo, hi); hi[1] += FV_PAD; }

// a ground-tree node: its square; y from its stitched geometry and the
// parent's surface (the geomorph target: the parent's index y range) ± the
// δ code's step
function groundBox(o, lo, hi, n, ix) {
  const s = NODE_M << n.k, geo = o.geometry;
  let y0, y1;
  if (geo.userData.y) [y0, y1] = geo.userData.y;
  else { const a = [0, Infinity, 0], b = [0, -Infinity, 0]; posBox(geo, a, b); y0 = a[1]; y1 = b[1]; }
  const e = ix.get(`${n.k}_${n.tx}_${n.ty}`), p = e && e.up;
  if (p) { y0 = Math.min(y0, p.y0 - DQ_PAD); y1 = Math.max(y1, p.y1 + DQ_PAD); }
  lo[0] = n.tx * s - 0.1; hi[0] = (n.tx + 1) * s + 0.1;
  lo[2] = n.ty * s - 0.1; hi[2] = (n.ty + 1) * s + 0.1;
  lo[1] = y0 - 0.1; hi[1] = y1 + 0.1;
}

// an InstancedMesh: every instance's transformed geometry box + the sway pad
function treeBox(o, lo, hi) {
  const g = [Infinity, Infinity, Infinity], G = [-Infinity, -Infinity, -Infinity];
  posBox(o.geometry, g, G);
  if (g[0] > G[0] || !o.count) return;
  const cx = (g[0] + G[0]) / 2, cy = (g[1] + G[1]) / 2, cz = (g[2] + G[2]) / 2;
  const hx = (G[0] - g[0]) / 2, hy = (G[1] - g[1]) / 2, hz = (G[2] - g[2]) / 2;
  const A = o.instanceMatrix.array;
  for (let i = 0; i < o.count; i++) {
    const k = 16 * i;
    for (let r = 0; r < 3; r++) {
      const c = A[k + r] * cx + A[k + 4 + r] * cy + A[k + 8 + r] * cz + A[k + 12 + r];
      const e = Math.abs(A[k + r]) * hx + Math.abs(A[k + 4 + r]) * hy + Math.abs(A[k + 8 + r]) * hz;
      if (c - e < lo[r]) lo[r] = c - e;
      if (c + e > hi[r]) hi[r] = c + e;
    }
  }
  xform(o.matrixWorld.elements, lo, hi);
  lo[0] -= SWAY_XZ; hi[0] += SWAY_XZ; lo[2] -= SWAY_XZ; hi[2] += SWAY_XZ; hi[1] += SWAY_Y;
}
